import type { ChangeEvent, InputHTMLAttributes } from 'react'
import { Box, TextField, type TextFieldProps } from '@mui/material'
import { formatDate } from '@/utils/date'

type NativeDateInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange' | 'disabled'
>

type DateTextFieldProps = Omit<TextFieldProps, 'type' | 'value' | 'onChange'> & {
  value?: string | null
  onChange: (value: string) => void
  nativeInputProps?: NativeDateInputProps
}

export default function DateTextField({
  value,
  onChange,
  disabled,
  fullWidth,
  inputProps,
  InputLabelProps,
  nativeInputProps,
  sx,
  ...textFieldProps
}: DateTextFieldProps) {
  const { style: nativeStyle, ...restNativeInputProps } = nativeInputProps ?? {}

  const handleNativeChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }

  return (
    <Box sx={[{ position: 'relative', width: fullWidth ? '100%' : undefined }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      <TextField
        {...textFieldProps}
        fullWidth={fullWidth}
        disabled={disabled}
        value={formatDate(value)}
        InputLabelProps={{ ...InputLabelProps, shrink: true }}
        inputProps={{ ...inputProps, placeholder: 'DD-MMM-YYYY', readOnly: true, tabIndex: -1 }}
        sx={{ width: '100%' }}
      />
      <input
        {...restNativeInputProps}
        type="date"
        value={value ?? ''}
        disabled={disabled}
        onChange={handleNativeChange}
        tabIndex={disabled ? -1 : nativeInputProps?.tabIndex}
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0,
          cursor: disabled ? 'default' : 'pointer',
          width: '100%',
          height: '100%',
          zIndex: 1,
          ...nativeStyle,
        }}
      />
    </Box>
  )
}
